import { t } from "i18next";
import type {
  ActionGroupConfig,
  ActionGroupStatus,
  BranchesActionGroupConfig,
  ConditionsConfig,
} from "@smartsuite/automation-engine-api";
import { findConfigGroupByInstanceId } from "../../../shared/config-walk";

export { findConfigGroupByInstanceId } from "../../../shared/config-walk";

// eslint-disable-next-line import/no-unused-modules -- consumed by later run-history tasks
export interface BranchView {
  key: string;
  label: string;
  isElse: boolean;
  /** Engine id for GetBranchData (api 7.25); "" on pre-7.25 jobs -> legacy resolution. */
  branchId: string;
  conditions?: ConditionsConfig;
  actionGroups: ActionGroupStatus[];
}

// eslint-disable-next-line import/no-unused-modules -- consumed by later run-history tasks
export interface BranchesBlockView {
  instanceId: number;
  label: string;
  branches: BranchView[];
  noneMatched: boolean;
}

const getBranchesConfig = (
  configGroups: ActionGroupConfig[],
  instanceId: number,
): BranchesActionGroupConfig | undefined => {
  const group = findConfigGroupByInstanceId(configGroups, instanceId);
  return group?.config.oneofKind === "branches"
    ? group.config.branches
    : undefined;
};

export const getBranchesBlockView = (
  statusGroup: ActionGroupStatus,
  configGroups: ActionGroupConfig[],
): BranchesBlockView | undefined => {
  if (statusGroup.status.oneofKind !== "branches") {
    return undefined;
  }
  const status = statusGroup.status.branches;
  const config = getBranchesConfig(configGroups, statusGroup.instanceId);

  // The engine reports ONLY branches that ran (verified against a real dev
  // payload: an else branch that didn't run is omitted entirely), so a
  // positional status[i] <-> config[i] join misattributes labels whenever an
  // earlier branch didn't match. Join by the instanceIds of each branch's
  // nested action groups instead. Only a status branch with NO nested groups
  // may fall back to the first unclaimed config branch: when it HAS groups
  // and none of their ids appear in any config branch, the snapshot genuinely
  // diverged, and claiming an arbitrary branch would show the wrong label AND
  // the wrong conditions -- default labels are the safer failure mode.
  const claimed = new Set<number>();
  const findConfigBranchIndex = (branchGroups: ActionGroupStatus[]): number => {
    const statusIds = new Set(branchGroups.map((group) => group.instanceId));
    const byIds = (config?.branches ?? []).findIndex(
      (candidate, index) =>
        !claimed.has(index) &&
        candidate.actionGroups.some((group) => statusIds.has(group.instanceId)),
    );
    if (byIds !== -1 || branchGroups.length > 0) {
      return byIds;
    }
    return (config?.branches ?? []).findIndex(
      (_, index) => !claimed.has(index),
    );
  };

  const branches: BranchView[] = status.branches
    .map((branch, index) => ({ branch, index }))
    .filter(({ branch }) => branch.conditionPassed)
    .map(({ branch, index }) => {
      const configIndex = findConfigBranchIndex(branch.actionGroups);
      if (configIndex !== -1) {
        claimed.add(configIndex);
      }
      const configBranch =
        configIndex === -1 ? undefined : config?.branches[configIndex];
      return {
        key: `branch-${configIndex === -1 ? `status-${index}` : configIndex}`,
        label:
          configBranch?.label ??
          t("branches.default-branch", {
            letter: String.fromCharCode(65 + index),
          }),
        isElse: false,
        branchId: branch.branchId,
        conditions: configBranch?.conditions,
        actionGroups: branch.actionGroups,
      };
    });

  // Per spec 7.4 the else branch runs exactly when no condition branch
  // matched, so presence of an else with zero matched branches means it ran.
  // conditionPassed on the else is intentionally not consulted (semantics to
  // re-verify against a real payload in plan Task 10).
  if (status.elseBranch !== undefined && branches.length === 0) {
    branches.push({
      key: "else",
      label: config?.elseBranch?.label ?? t("branches.if-no-conditions-met"),
      isElse: true,
      branchId: status.elseBranch.branchId,
      actionGroups: status.elseBranch.actionGroups,
    });
  }

  return {
    instanceId: statusGroup.instanceId,
    label: config?.label ?? t("branches.default-block-name"),
    branches,
    noneMatched: branches.length === 0,
  };
};
